import React, { useEffect, useState } from 'react';
import Link from '../general/Link';
import Tooltip from '../general/Tooltip';
import { NovaConversationSummary } from '@/types';
import NovaLogoMark from './NovaLogoMark';
import NovaThemeToggle from './NovaThemeToggle';
import NovaHistorySidebar from './NovaHistorySidebar';
import { novaThemes, NovaThemeName } from './novaTheme';
import {
  NovaAttachIcon,
  NovaReportIcon,
  NovaCompassIcon,
  NovaFolderIcon,
  NovaHelpCircleIcon,
  NovaCollapseIcon,
  NovaHistoryIcon,
  NovaMenuIcon,
} from './novaIcons';

const REPORT_GENERATOR_HREF = '/build-report';
const DISCOVER_HREF = '/discover';
const DOCUMENTS_HREF = '/documents';

type NovaNavKey = 'templates' | 'discover' | 'documents';

const NAV_ITEMS: { key: NovaNavKey; href: string; label: string; Icon: typeof NovaReportIcon }[] = [
  { key: 'templates', href: REPORT_GENERATOR_HREF, label: 'Build a Report', Icon: NovaReportIcon },
  { key: 'discover', href: DISCOVER_HREF, label: 'Discover', Icon: NovaCompassIcon },
  { key: 'documents', href: DOCUMENTS_HREF, label: 'Documents', Icon: NovaFolderIcon },
];

interface NovaLandingSidebarProps {
  theme: NovaThemeName;
  collapsed: boolean;
  onCollapse: () => void;
  onExpand: () => void;
  onNewChat: () => void;
  conversations: NovaConversationSummary[];
  conversationsLoading: boolean;
  activeConversationId?: string;
  onSelectConversation: (conversationId: string) => void;
  onDeleteConversation: (conversationId: string) => void;
  userName?: string;
  /** Which rail item reflects the page currently on screen. Unset (e.g. the chat landing) highlights none. */
  active?: NovaNavKey;
  onNavClick?: (key: NovaNavKey) => void;
}

// The Nova landing page's persistent rail — logo/theme toggle, new chat, nav links,
// recent chat history and the account row — kept as its own component (rather than
// inline in NovaLanding) so its layout/scroll behaviour can be styled and reasoned
// about independently of the main answer column.
const NovaLandingSidebar: React.FC<NovaLandingSidebarProps> = ({
  theme,
  collapsed,
  onCollapse,
  onExpand,
  onNewChat,
  conversations,
  conversationsLoading,
  activeConversationId,
  onSelectConversation,
  onDeleteConversation,
  userName,
  active,
  onNavClick,
}) => {
  const surfaces = novaThemes[theme];
  // Below md the rail is replaced by a slim top bar whose menu button opens the full
  // rail as a drawer, so phones still get New chat, the nav links and chat history.
  const [mobileOpen, setMobileOpen] = useState(false);
  const closeMobile = () => setMobileOpen(false);

  useEffect(() => {
    if (!mobileOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMobileOpen(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [mobileOpen]);

  const renderExpandedContent = (mobile: boolean) => (
    <>
      <div className={`flex shrink-0 items-center justify-between px-2 ${mobile ? 'mb-2' : 'mb-[18px]'}`}>
        {!mobile && <NovaLogoMark theme={theme} height={22} />}
        <div className="ml-auto flex items-center gap-1.5">
          {!mobile && <NovaThemeToggle segmentWidth={26} segmentHeight={22} iconSize={13} />}
          <button
            type="button"
            aria-label={mobile ? 'Close menu' : 'Collapse sidebar'}
            title="Collapse"
            onClick={mobile ? closeMobile : onCollapse}
            className="flex h-[30px] w-[30px] cursor-pointer items-center justify-center rounded-[9px] transition-opacity hover:opacity-70"
            style={{ color: surfaces.ink.chromeIcon }}
          >
            <NovaCollapseIcon className="h-4 w-4" />
          </button>
        </div>
      </div>

      <button
        type="button"
        onClick={() => {
          onNewChat();
          closeMobile();
        }}
        className="mb-[14px] flex h-[42px] w-full shrink-0 cursor-pointer items-center gap-2.5 rounded-[11px] px-3.5 text-[15px] font-semibold"
        style={{ background: surfaces.actionAccent, color: surfaces.ink.onGold }}
      >
        <NovaAttachIcon className="h-4 w-4" />
        New chat
      </button>

      <div className="mb-[18px] flex shrink-0 flex-col gap-px">
        {NAV_ITEMS.map(({ key, href, label, Icon }) => (
          <Link
            key={key}
            link={{ href, text: label }}
            onClick={() => {
              onNavClick?.(key);
              closeMobile();
            }}
            className="flex items-center gap-[11px] rounded-[9px] px-3 py-2.5 text-[14.5px]"
            style={{
              background: key === active ? surfaces.surfaces.activeRow : undefined,
              color: key === active ? surfaces.ink.heading : surfaces.ink.secondary,
              fontWeight: key === active ? 500 : undefined,
            }}
          >
            <Icon className="h-4 w-4" />
            {label}
          </Link>
        ))}
      </div>

      <div className="mb-2 shrink-0 px-2 text-xs tracking-[0.08em] uppercase" style={{ color: surfaces.ink.eyebrow }}>
        Chat history
      </div>
      {/* Fills the space down to the Help & support divider — NovaHistorySidebar shows
          as many rows as fit and pins "View all" to the bottom (scrolling only once expanded). */}
      <div className="mb-2 min-h-0 flex-1">
        <NovaHistorySidebar
          conversations={conversations}
          loading={conversationsLoading}
          activeConversationId={activeConversationId}
          onSelect={(conversationId) => {
            onSelectConversation(conversationId);
            closeMobile();
          }}
          onDelete={onDeleteConversation}
          theme={theme}
          compact
          fill
        />
      </div>

      <div
        className="flex shrink-0 flex-col gap-px border-t pt-2.5"
        style={{ borderColor: surfaces.surfaces.hairline }}
      >
        <Link
          link={{ href: 'https://stylus.com/contact-us', text: 'Help & support', target: '_blank' }}
          className="flex items-center gap-[11px] rounded-[9px] px-3 py-2.5 text-[14.5px]"
          style={{ color: surfaces.ink.secondary }}
        >
          <NovaHelpCircleIcon className="h-4 w-4" />
          Help &amp; support
        </Link>
        <div className="flex items-center px-3 pt-2 pb-0.5">
          <span className="text-[13.5px]" style={{ color: surfaces.ink.secondary }}>
            {userName || 'Guest'} · Stylus
          </span>
        </div>
      </div>
    </>
  );

  const mobileChrome = (
    <>
      <div
        className="flex h-[52px] shrink-0 items-center gap-2 px-3 md:hidden"
        style={{ background: surfaces.surfaces.field, borderBottom: `1px solid ${surfaces.surfaces.panelBorder}` }}
      >
        <button
          type="button"
          aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={mobileOpen}
          onClick={() => setMobileOpen((open) => !open)}
          className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-[10px]"
          style={{ color: surfaces.ink.chromeIcon }}
        >
          <NovaMenuIcon className="h-5 w-5" />
        </button>
        <NovaLogoMark theme={theme} height={20} />
        <span className="flex-1" />
        <NovaThemeToggle segmentWidth={26} segmentHeight={22} iconSize={13} />
        <button
          type="button"
          aria-label="New chat"
          onClick={onNewChat}
          className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-[10px]"
          style={{ background: surfaces.actionAccent, color: surfaces.ink.onGold }}
        >
          <NovaAttachIcon className="h-4 w-4" />
        </button>
      </div>

      {mobileOpen && (
        <div className="fixed inset-x-0 top-[var(--header-height)] bottom-0 z-40 flex md:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={closeMobile} aria-hidden />
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Nova menu"
            className="relative flex h-full w-[300px] max-w-[85vw] flex-col p-[14px] pt-4 pb-[18px] shadow-xl"
            style={{ background: surfaces.surfaces.field }}
            data-lenis-prevent
          >
            {renderExpandedContent(true)}
          </div>
        </div>
      )}
    </>
  );

  if (collapsed) {
    return (
      <>
        {mobileChrome}
        <div
          // Deliberately not overflow-y-auto: this rail's content is a small fixed
          // set of icons (~360px tall) that fits any realistic desktop viewport (this
          // rail only renders at md+), and overflow-y-auto here forces overflow-x to
          // clip too (they can't be set independently per the CSS overflow spec),
          // which clipped the right-placed hover tooltips down to a sliver. If this
          // ever needs to scroll again, the tooltips will need to render through a
          // portal rather than reintroducing overflow-y-auto here.
          className="hidden w-[60px] shrink-0 flex-col items-center gap-1.5 p-2.5 pt-5 pb-[18px] md:flex"
          style={{ background: surfaces.surfaces.field, borderRight: `1px solid ${surfaces.surfaces.panelBorder}` }}
        >
          <button
            type="button"
            aria-label="Expand sidebar"
            title="Expand"
            onClick={onExpand}
            className="mb-2 flex h-[30px] w-[30px] cursor-pointer items-center justify-center rounded-[9px] transition-opacity hover:opacity-70"
            style={{ color: surfaces.ink.chromeIcon }}
          >
            <NovaCollapseIcon className="h-4 w-4 rotate-180" />
          </button>

          <Tooltip placement="right" content="New chat">
            <button
              type="button"
              aria-label="New chat"
              onClick={onNewChat}
              className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-[10px]"
              style={{ background: surfaces.actionAccent, color: surfaces.ink.onGold }}
            >
              <NovaAttachIcon className="h-4 w-4" />
            </button>
          </Tooltip>

          {NAV_ITEMS.map(({ key, href, label, Icon }) => (
            <Tooltip key={key} placement="right" content={label}>
              <Link
                link={{ href, text: label }}
                onClick={() => onNavClick?.(key)}
                className="flex h-9 w-9 items-center justify-center rounded-[10px]"
                style={{
                  background: key === active ? surfaces.surfaces.activeRow : undefined,
                  color: key === active ? surfaces.ink.heading : surfaces.ink.secondary,
                }}
              >
                <Icon className="h-4 w-4" />
              </Link>
            </Tooltip>
          ))}

          <Tooltip placement="right" content="Chat history">
            <button
              type="button"
              aria-label="Chat history"
              onClick={onExpand}
              className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-[10px]"
              style={{ color: surfaces.ink.secondary }}
            >
              <NovaHistoryIcon className="h-4 w-4" />
            </button>
          </Tooltip>

          <span className="flex-1" />

          <Tooltip placement="right" content="Help & support">
            <Link
              link={{ href: 'https://stylus.com/contact-us', text: 'Help & support', target: '_blank' }}
              className="flex h-9 w-9 items-center justify-center rounded-[10px]"
              style={{ color: surfaces.ink.secondary }}
            >
              <NovaHelpCircleIcon className="h-4 w-4" />
            </Link>
          </Tooltip>
        </div>
      </>
    );
  }

  return (
    <>
      {mobileChrome}
      <div
        className="hidden w-[264px] shrink-0 flex-col p-[14px] pt-5 pb-[18px] md:flex"
        style={{ background: surfaces.surfaces.field, borderRight: `1px solid ${surfaces.surfaces.panelBorder}` }}
      >
        {renderExpandedContent(false)}
      </div>
    </>
  );
};

export default NovaLandingSidebar;
